import { Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { City } from '@model/ws-common/city.model';
import { Country } from '@model/ws-common/country.model';
import { forkJoin, Subscription } from 'rxjs';

import { ApiResponse } from '@model/api-response.model';
import { LocationService } from 'src/app/services/ws-common/location/location.service';

@Component({
  selector: 'app-country-input',
  templateUrl: './country-input.component.html',
  styleUrls: ['./country-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountryInputComponent),
      multi: true,
    }
  ],
})
export class CountryInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private subscriptions: Subscription = new Subscription();
  allCountries: Country[] = [];
  countriesFiltered: Country[] = [];
  allCities: City[] = [];
  citiesFiltered: City[] = [];
  @Input() valid!: boolean;
  @Input() multi = true;
  @Input() userLocations: string[] = [];
  @ViewChild('countryInput') countryInput!: ElementRef;
  @ViewChild('cityInput') cityInput!: ElementRef;
  selectedCities: City[] = [];
  selectedCountries: Country[] = [];
  loading = false;
  public _value: string[] = [];
  public disabled!: boolean;

  constructor(private locationService: LocationService) { }

  ngOnInit(): void {
    this.getAllCountries();
  }

  countryChanges(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value || '';
    this.countriesFiltered = this._filterCountry(inputValue);
  }

  cityChanges(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value || '';
    this.citiesFiltered = this._filterCity(inputValue);
  }

  private _filterCity(value: string): City[] {
    const filterValue = value.toLowerCase();
    return this.allCities
      .filter(city => city.cityTitle.toLowerCase().includes(filterValue))
      .sort((c1, c2) => {
        const c1StartsWith = c1.cityTitle.toLowerCase().startsWith(filterValue);
        const c2StartsWith = c2.cityTitle.toLowerCase().startsWith(filterValue);
        if (c1StartsWith && !c2StartsWith) return -1;
        if (!c1StartsWith && c2StartsWith) return 1;
        return c1.cityTitle.localeCompare(c2.cityTitle);
      });
  }

  private _filterCountry(value: string): Country[] {
    const filterValue = value.toLowerCase();
    return this.allCountries
      .filter(country => country.countryTitle.toLowerCase().includes(filterValue))
      .sort((c1, c2) => {
        const c1StartsWith = c1.countryTitle.toLowerCase().startsWith(filterValue);
        const c2StartsWith = c2.countryTitle.toLowerCase().startsWith(filterValue);
        if (c1StartsWith && !c2StartsWith) return -1;
        if (!c1StartsWith && c2StartsWith) return 1;
        return c1.countryTitle.localeCompare(c2.countryTitle);
      });
  }

  private getAllCountries() {
    this.loading = true;
    this.subscriptions.add(
      this.locationService.getCountries().subscribe(res => {
        this.allCountries = res.data;
        const sortedAlphabetically = this.allCountries.sort((c1, c2) => (c1.countryTitle < c2.countryTitle) ? -1 : 1);
        this.allCountries = sortedAlphabetically;
        if (this.userLocations.length === 0) {
          this.loading = false;
        }
        this.getUserData();
      }));
  }

  onChanged: (value: string[]) => void = () => { };
  onTouched: () => void = () => { };

  writeValue(val: string[]): void {
    this._value = val;
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private getUserData() {
    const observables = this.userLocations.map((city: string) => this.locationService.getInfoByCityId(city));
    forkJoin(observables).subscribe((responses: any) => {
      responses.forEach((res: ApiResponse<City>) => {
        this.selectedCities.push(res.data);
        this.allCountries.forEach(country => {
          if (country.id === res.data.countryId) {
            if (!this.selectedCountries.includes(country)) {
              this.selectedCountries.push(country);
            }
          }
        });
      });
      this.getAllCities();
    });
  }

  private getAllCities() {
    this.allCities = [];
    let obs = this.selectedCountries.map((country: Country) => this.locationService.getCities(country.id));
    forkJoin(obs).subscribe((res: ApiResponse<City[]>[]) => {
      res.forEach((obj: ApiResponse<City[]>) => {
        this.allCities.push(...obj.data);
      });
      const sortedAlphabetically = this.allCities.sort((c1, c2) => (c1.cityTitle < c2.cityTitle) ? -1 : 1);
      this.allCities = sortedAlphabetically;
      this.loading = false;
    });
  }

  addCountry(option: MatAutocompleteSelectedEvent) {
    this.loading = true;
    let newCountry = option.option.value;
    if (this.selectedCountries.some(country => country.id === newCountry.id)) {
      this.loading = false;
      return;
    }
    if (!this.multi) {
      this.addSingleCountry(newCountry);
      return;
    }
    this.locationService.getCities(newCountry.id).subscribe((res: ApiResponse<City[]>) => {
      this.selectedCountries.push(newCountry);
      this.allCities.push(...res.data);
      const sortedAlphabetically = this.allCities.sort((c1, c2) => (c1.cityTitle < c2.cityTitle) ? -1 : 1);
      this.allCities = sortedAlphabetically;
      this.loading = false;
    });
    const indexToRemove = this.allCountries.indexOf(newCountry);
    if (indexToRemove !== -1) this.allCountries.splice(indexToRemove, 1);
    this.countryInput.nativeElement.value = '';
    this.countriesFiltered = this._filterCountry(this.countryInput.nativeElement.value);
  }

  filterCitiesBasedOnInput(userInput: string): City[] {
    const lowerCaseInput = userInput.toLowerCase();

    return this.allCities
      .filter(city => city.cityTitle.toLowerCase().includes(lowerCaseInput))
      .sort((c1, c2) => {
        const c1StartsWith = c1.cityTitle.toLowerCase().startsWith(lowerCaseInput);
        const c2StartsWith = c2.cityTitle.toLowerCase().startsWith(lowerCaseInput);
        if (c1StartsWith && !c2StartsWith) return -1;
        if (!c1StartsWith && c2StartsWith) return 1;
        return c1.cityTitle.localeCompare(c2.cityTitle);
      });
  }

  addSingleCountry(newCountry: Country) {
    if (this.selectedCountries[0]) this.removeCountry(this.selectedCountries[0]);
    this.selectedCountries = [newCountry];
    this.locationService.getCities(newCountry.id).subscribe((res: ApiResponse<City[]>) => {
      this.allCities = res.data;
      const sortedAlphabetically = this.allCities.sort((c1, c2) => (c1.cityTitle < c2.cityTitle) ? -1 : 1);
      this.allCities = sortedAlphabetically;
      this.loading = false;
    });
    const indexToRemove = this.allCountries.indexOf(newCountry);
    if (indexToRemove !== -1) this.allCountries.splice(indexToRemove, 1);
    this.countryInput.nativeElement.value = '';
    this.countriesFiltered = this._filterCountry(this.countryInput.nativeElement.value);
  }

  addCity(option: MatAutocompleteSelectedEvent) {
    let newCity = option.option.value;
    if (this.selectedCities.some(city => city.id === newCity.id)) {
      return;
    }
    if (!this.multi) {
      this.selectedCities = [newCity];
    } else {
      this.selectedCities.push(newCity);
    }
    this.cityInput.nativeElement.value = '';
    this.citiesFiltered = this._filterCity(this.cityInput.nativeElement.value);

    let ids = this.selectedCities.map(c => c.id);
    this.onTouched();
    this.onChanged(ids);
  }

  removeCountry(countryToRemove: Country) {
    this.selectedCountries = this.selectedCountries.filter(country => country !== countryToRemove);
    this.allCountries.push(countryToRemove);
    this.selectedCities = this.selectedCities.filter(c => c.countryId !== countryToRemove.id);
    let ids = this.selectedCities.map(c => c.id);
    this.onTouched();
    this.onChanged(ids);
    this.allCities = this.allCities.filter(c => c.countryId !== countryToRemove.id);
    this.citiesFiltered = this._filterCity('');
  }

  removeCity(cityToRemove: City) {
    this.selectedCities = this.selectedCities.filter(c => c !== cityToRemove);
    let ids = this.selectedCities.map(c => c.id);
    this.onTouched();
    this.onChanged(ids);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
